import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { showcaseApi } from '../api/showcaseApi';
import type {
  ApiError,
  OutboxRecordRequest,
  OutboxRecordResponse,
  OutboxRelayedResponse,
  OutboxStatusResponse,
} from '../types/showcase';

/**
 * GET /api/eraf/showcase/demo/outbox/status — C056 콘솔 상태
 * queryKey: ['common-showcase', 'outbox', 'status']
 */
export function useOutboxStatus() {
  return useQuery<OutboxStatusResponse, ApiError>({
    queryKey: ['common-showcase', 'outbox', 'status'],
    queryFn: () => showcaseApi.outboxStatus(),
    staleTime: 15_000,
  });
}

/**
 * POST /api/eraf/showcase/demo/outbox/record — 메시지 아웃박스 적재 (enqueue, 발행 아님)
 * 성공 시 status 캐시 무효화 (pendingCount 등 갱신)
 */
export function useOutboxRecord() {
  const queryClient = useQueryClient();
  return useMutation<OutboxRecordResponse, ApiError, OutboxRecordRequest>({
    mutationFn: (req) => showcaseApi.outboxRecord(req),
    mutationKey: ['common-showcase', 'outbox', 'record'],
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['common-showcase', 'outbox', 'status'] });
    },
  });
}

/** ~30s 상한 (릴레이 initial-delay 10s + poll-interval 5s 감안 · 5s 간격 최대 6회 폴링) */
const OUTBOX_MAX_POLL_ATTEMPTS = 6;

/**
 * GET /api/eraf/showcase/demo/outbox/relayed/{outboxId} — recorded→published→consumed 단계 진행 조회
 * queryKey: ['common-showcase', 'outbox', 'relayed', outboxId]
 * enabled: outboxId 존재 시에만. consumed=true 이거나 최대 폴링 횟수 도달 시 자동 중단.
 */
export function useOutboxRelayed(outboxId: number | null) {
  return useQuery<OutboxRelayedResponse, ApiError>({
    queryKey: ['common-showcase', 'outbox', 'relayed', outboxId],
    queryFn: () => showcaseApi.outboxRelayed(outboxId!),
    enabled: outboxId !== null,
    staleTime: 0,
    refetchInterval: (query) => {
      if (query.state.data?.consumed) return false;
      if (query.state.dataUpdateCount >= OUTBOX_MAX_POLL_ATTEMPTS) return false;
      return 5_000;
    },
  });
}
